import {
    Autocomplete,
    Card,
    Chip,
    Dialog,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    ORTHOIMAGE_RGB_TGDT_UUID,
    PAGINATION,
    PIPELINE_INSTANCE_STATUS,
} from "../../../constants";
import {
    createSortSearchParams,
    objectArrayStringify,
    userNamesComparator,
} from "../../utils";
import { useCallback, useMemo, useState } from "react";
import {
    useFetch,
    useIsBasicRole,
    useSnackbar,
    useUsers,
} from "../../../hooks";

import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddLinkIcon from "@mui/icons-material/AddLink";
import { AlignmentModal } from "../../alignment-report";
import { BACKEND_ROUTES } from "../../../backendRoutes";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import { FRONTEND_ROUTES } from "../../../frontendRoutes";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import { PipelineInstanceStatusPieChart } from "../PipelineInstanceStatusPieChart";
import PublicIcon from "@mui/icons-material/Public";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { ReportStatus } from "./ReportStatus";
import { RoutingDialog } from "../../monitoring/routing";
import Scrollbar from "../../../components/Scrollbar";
import { TrafficTableSkeleton } from "../TrafficTableSkeleton";
import { formatDateTime } from "../../../utils/formatTime";
import { laggy } from "../../../utils/SWRLaggy";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";

export function InboundTrafficTable() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const isBasicRole = useIsBasicRole();
    const theme = useTheme();

    const [visibilityModel, setVisibilityModel] = useState({
        reportStatus: true,
        pipelineInstanceStatusCounts: true,
        company: true,
        campaign: true,
        siteDisplay: true,
        site: false,
        acquisitionDate: true,
        uploadDate: false,
        receptionTime: true,
        dataType: true,
        acquisitionVector: true,
        processingLeadUuid: false,
        status: false,
        actions: true,
    });

    const [{ sortModel, paginationModel }, setGridState] = useState({
        ...initialState.sorting,
        ...initialState.pagination,
    });

    const [openEditMission, setOpenEditMission] = useState(false);
    const [editMissionUuid, setEditMissionUuid] = useState(null);

    const alignmentReportMissionUuid = searchParams.get(
        "alignmentReportMissionUuid"
    );
    const alignmentModalOnClose = () => {
        setSearchParams((prevState) => {
            const newSearchParams = new URLSearchParams(prevState);
            newSearchParams.delete("alignmentReportMissionUuid");
            return newSearchParams;
        });
    };

    const filterParams = {
        contractUuid: searchParams.get("contractUuid"),
        companyUuid: searchParams.get("companyUuid"),
        siteUuid: searchParams.get("siteUuid"),
        processingLeadUuid: searchParams.get("processingLeadUuid"),
        showAllTraffic: searchParams.get("showAllTraffic") === "true",
        pipelineInstanceStatus: searchParams.get("pipelineInstanceStatus"),
    };

    const missionsSearchParams = new URLSearchParams({
        pipelineInstancesTGDTUuid: ORTHOIMAGE_RGB_TGDT_UUID,
        pipelineInstanceStatusCounts: true,
        associationCompleted: false,
        parentInfo: true,
        experimentMissions: true,
        acquisitionVector: true,
        limit: pageItemCount,
        offset: paginationModel.page * pageItemCount,
        ...(filterParams.contractUuid && {
            Site_contractUuid: filterParams.contractUuid,
        }),
        ...(filterParams.companyUuid && {
            Site_Contract_companyUuid: filterParams.companyUuid,
        }),
        ...(filterParams.siteUuid && { siteUuid: filterParams.siteUuid }),
        ...(filterParams.processingLeadUuid && {
            processingLeadUuid: filterParams.processingLeadUuid,
        }),
        ...(filterParams.pipelineInstanceStatus && {
            pipelineInstanceStatus: filterParams.pipelineInstanceStatus,
        }),
        onlySupervisedMissions: !filterParams.showAllTraffic,
        ...createSortSearchParams(sortModel, sortMap),
    });

    const {
        data: missions,
        error: missionsFetchError,
        isLagging,
        mutate,
    } = useSWR(`${BACKEND_ROUTES.MISSION}?${missionsSearchParams}`, {
        use: [laggy],
    });

    const { data: companies, error: companiesFetchError } = useSWRImmutable(
        `${BACKEND_ROUTES.COMPANY}?sort=name&limit=${PAGINATION.COMPANY.LIMIT.MAX}`
    );

    const { data: contracts, error: contractsFetchError } = useSWRImmutable(
        `${BACKEND_ROUTES.CONTRACT}?sort=name${filterParams.companyUuid ? `&companyUuid=${filterParams.companyUuid}` : ""}&limit=${PAGINATION.CONTRACT.LIMIT.MAX}`
    );

    const { data: sites, error: sitesFetchError } = useSWRImmutable(
        filterParams.contractUuid
            ? `${BACKEND_ROUTES.SITE}?sort=name&contractUuid=${filterParams.contractUuid}`
            : null
    );

    const { data: users, error: usersFetchError } = useUsers();

    const { openSnackbar } = useSnackbar();
    const { get, patch } = useFetch();

    const handleProcessRowUpdateError = useCallback(
        (error) => openSnackbar(error.message, "error"),
        [openSnackbar]
    );

    const updatePagination = useCallback(
        (newPaginationModel) =>
            setGridState((prevState) => ({
                ...prevState,
                paginationModel: newPaginationModel,
            })),
        []
    );

    const updateSort = useCallback(
        (newSortModel) =>
            setGridState((prevState) => ({
                ...prevState,
                sortModel: newSortModel,
            })),
        []
    );

    const processRowUpdate = useCallback(
        async (newRow) => {
            const updateMission = patch(
                `${BACKEND_ROUTES.MISSION}/${newRow.uuid}`,
                {
                    body: {
                        processingLeadUuid:
                            newRow.processingLeadUuid === invalidUuid
                                ? null
                                : newRow.processingLeadUuid,
                    },
                    forwardError: true,
                }
            );
            await mutate(updateMission, { populateCache: false });

            openSnackbar(
                `Updated Mission of ${formatDateTime(
                    newRow.acquisitionDate
                )} successfully.`,
                "success"
            );

            return newRow;
        },
        [mutate, openSnackbar, patch]
    );

    const rows = useMemo(
        () =>
            missions?.rows.map((mission) => ({
                id: mission.uuid,
                uuid: mission.uuid,
                site: mission.Site.name,
                siteDisplay: mission.Site.displayName,
                dataType: objectArrayStringify(
                    mission.AcquisitionVector.SensorBundles,
                    ["Sensor", "dataType"]
                ),
                acquisitionDate: new Date(mission.date),
                uploadDate: new Date(mission.createdAt),
                processingLeadUuid: mission.processingLeadUuid ?? invalidUuid,
                company: mission.Site.Contract.Company.name,
                campaign: mission.Site.Contract.name,
                acquisitionVector: mission.AcquisitionVector.name,
                status: mission.preProcessingStatus,
                experiments: mission.Site.Experiments,
                reportStatus: mission.reportStatus,
                receptionTime: new Date(mission.createdAt),
                latestRgbOrthoimagePipelineId:
                    mission.AssociatedTraitGroups[0]?.PipelineInstances[0]
                        ?.uuid,
                pipelineInstanceStatusCounts:
                    mission.pipelineInstanceStatusCounts,
            })),
        [missions]
    );

    const columns = useMemo(
        () =>
            users && [
                {
                    field: "reportStatus",
                    headerName: "Report",
                    minWidth: 60,
                    flex: 0.5,
                    sortable: false,
                    renderCell: (params) =>
                        params.value ? (
                            <ReportStatus status={params.value} />
                        ) : (
                            <Typography
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                    height: "100%",
                                }}
                            >
                                -
                            </Typography>
                        ),
                    filterable: false,
                },
                {
                    field: "pipelineInstanceStatusCounts",
                    headerName: "Orthoimage",
                    minWidth: 80,
                    flex: 0.5,
                    sortable: false,
                    filterable: false,
                    renderCell: (params) => {
                        const counts = params.value;
                        if (!counts) return null;

                        const data = [
                            {
                                label: PIPELINE_INSTANCE_STATUS.PLANNED,
                                value:
                                    counts[PIPELINE_INSTANCE_STATUS.PLANNED] ??
                                    0,
                                color: theme.palette.grey[400],
                            },
                            {
                                label: PIPELINE_INSTANCE_STATUS.ONGOING,
                                value:
                                    counts[PIPELINE_INSTANCE_STATUS.ONGOING] ??
                                    0,
                                color: theme.palette.info.main,
                            },
                            {
                                label: PIPELINE_INSTANCE_STATUS.SUSPENDED,
                                value:
                                    counts[
                                        PIPELINE_INSTANCE_STATUS.SUSPENDED
                                    ] ?? 0,
                                color: theme.palette.warning.main,
                            },
                            {
                                label: PIPELINE_INSTANCE_STATUS.FINISHED,
                                value:
                                    counts[PIPELINE_INSTANCE_STATUS.FINISHED] ??
                                    0,
                                color: theme.palette.success.main,
                            },
                            {
                                label: PIPELINE_INSTANCE_STATUS.ERROR,
                                value:
                                    counts[PIPELINE_INSTANCE_STATUS.ERROR] ?? 0,
                                color: theme.palette.error.main,
                            },
                            {
                                label: PIPELINE_INSTANCE_STATUS.ABORTED,
                                value:
                                    counts[PIPELINE_INSTANCE_STATUS.ABORTED] ??
                                    0,
                                color: theme.palette.error.dark,
                            },
                        ];

                        const total = data.reduce(
                            (sum, item) => sum + item.value,
                            0
                        );

                        if (total === 0)
                            return (
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        width: "100%",
                                    }}
                                >
                                    <Tooltip
                                        title="No processing"
                                        placement="right"
                                    >
                                        <IconButton
                                            sx={{
                                                fontSize: 40,
                                                lineHeight: 1,
                                            }}
                                            onClick={() => {
                                                setSearchParams((prevState) => {
                                                    const newSearchParams =
                                                        new URLSearchParams(
                                                            prevState
                                                        );

                                                    newSearchParams.set(
                                                        "alignmentReportMissionUuid",
                                                        params.row.id
                                                    );

                                                    return newSearchParams;
                                                });
                                            }}
                                        >
                                            &#x2300;
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            );

                        return (
                            <div
                                onClick={() => {
                                    setSearchParams((prevState) => {
                                        const newSearchParams =
                                            new URLSearchParams(prevState);

                                        newSearchParams.set(
                                            "alignmentReportMissionUuid",
                                            params.row.id
                                        );

                                        return newSearchParams;
                                    });
                                }}
                                style={{ cursor: "pointer" }}
                            >
                                <PipelineInstanceStatusPieChart
                                    pipelineInstancesCountPerStatus={data}
                                />
                            </div>
                        );
                    },
                },
                {
                    field: "actions",
                    headerName: "Actions",
                    type: "actions",
                    minWidth: 220,
                    flex: 2,
                    getActions: (params) => {
                        return [
                            <Tooltip title="Acquisition report">
                                <GridActionsCellItem
                                    label="Acquisition report"
                                    icon={<QueryStatsIcon />}
                                    component={Link}
                                    to={`/${FRONTEND_ROUTES.TRAFFIC}/${FRONTEND_ROUTES.INBOUND_TRAFFIC}/${FRONTEND_ROUTES.ACQUISITION_REPORT}/${params.row.id}`}
                                />
                            </Tooltip>,
                            ...(!isBasicRole
                                ? [
                                      <Tooltip title="Go to Plot Map Tool">
                                          <GridActionsCellItem
                                              label="Go to Plot Map Tool"
                                              icon={<PublicIcon />}
                                              onClick={() => {
                                                  get(
                                                      `${BACKEND_ROUTES.SITE}?sort=name&name=${params.row.site}`
                                                  )
                                                      .then((data) => {
                                                          if (
                                                              data.rows &&
                                                              data.rows.length >
                                                                  0
                                                          ) {
                                                              const siteUuid =
                                                                  data.rows[0]
                                                                      .uuid;
                                                              navigate(
                                                                  `/${FRONTEND_ROUTES.PARCELLAIRE}/${params.row.uuid}?missionUuid=${params.row.uuid}&siteUuid=${siteUuid}&fastOrthoimage=true`
                                                              );
                                                          }
                                                      })
                                                      .catch((error) =>
                                                          console.error(
                                                              "Error fetching site:",
                                                              error
                                                          )
                                                      );
                                              }}
                                          />
                                      </Tooltip>,
                                  ]
                                : []),
                            <GridActionsCellItem
                                label="Edit Mission"
                                icon={<EditIcon />}
                                onClick={() => {
                                    setEditMissionUuid(params.row.uuid);
                                    setOpenEditMission(!openEditMission);
                                }}
                                showInMenu
                            />,
                            <Tooltip title="Pipeline Instance">
                                <GridActionsCellItem
                                    disabled={
                                        !params.row
                                            .latestRgbOrthoimagePipelineId
                                    }
                                    label="Pipeline Instance"
                                    icon={<AccountTreeIcon />}
                                    component={Link}
                                    to={`/${FRONTEND_ROUTES.TRAFFIC}/${FRONTEND_ROUTES.PIPELINE_INSTANCE}/${params.row.latestRgbOrthoimagePipelineId}`}
                                />
                            </Tooltip>,
                            <GridActionsCellItem
                                label="Copy mission uuid"
                                icon={<ContentCopyIcon />}
                                onClick={() => {
                                    navigator.clipboard.writeText(
                                        params.row.uuid
                                    );
                                }}
                                showInMenu
                            />,
                            <GridActionsCellItem
                                label={
                                    params.row.experiments.length
                                        ? "Associate"
                                        : "Experiments are missing"
                                }
                                icon={<AddLinkIcon />}
                                disabled={!params.row.experiments.length}
                                component={Link}
                                to={`/${FRONTEND_ROUTES.TRAFFIC}/${FRONTEND_ROUTES.INBOUND_TRAFFIC}/${FRONTEND_ROUTES.INBOUND_TRAFFIC_DETAILS}/${params.row.id}`}
                                showInMenu
                            />,
                        ];
                    },
                    filterable: false,
                },
                {
                    field: "company",
                    headerName: "Company",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "campaign",
                    headerName: "Campaign",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "site",
                    headerName: "Site name",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "siteDisplay",
                    headerName: "Site display name",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "acquisitionDate",
                    type: "date",
                    headerName: "Acquisition Date",
                    minWidth: 140,
                    flex: 2,
                    filterable: false,
                },
                {
                    field: "uploadDate",
                    type: "date",
                    headerName: "Upload Date",
                    minWidth: 140,
                    flex: 2,
                    filterable: false,
                },
                {
                    field: "receptionTime",
                    headerName: "Received",
                    minWidth: 100,
                    flex: 2,
                    filterable: false,
                    renderCell: (params) => {
                        const mission = missions?.rows.find(
                            (m) => m.uuid === params.row.uuid
                        );

                        if (
                            mission?.status === "Delivered" ||
                            mission?.status === "Aborted"
                        ) {
                            return null;
                        }

                        const uploadDate = params.value;
                        const todayDate = new Date();
                        const diffInMs = todayDate - uploadDate;
                        const diffInDays = Math.floor(
                            diffInMs / (1000 * 60 * 60 * 24)
                        );

                        let label = "";
                        let color = "";
                        let customSx = {};

                        if (diffInDays < 5) {
                            label =
                                diffInDays === 0
                                    ? "Today"
                                    : diffInDays === 1
                                      ? `${diffInDays} day`
                                      : `${diffInDays} days`;
                            color = "success";
                            customSx = { color: "#fff" };
                        } else if (diffInDays < 10) {
                            label = `${diffInDays} days`;
                            color = "warning";
                            customSx = { color: "#fff" };
                        } else if (diffInDays < 20) {
                            label = `${diffInDays} days`;
                            color = "default";
                            customSx = {
                                backgroundColor: "#f5822a",
                                color: "#fff",
                            };
                        } else {
                            label = `${diffInDays} days`;
                            color = "error";
                            customSx = { color: "#fff" };
                        }

                        return (
                            <Chip
                                label={label}
                                color={color}
                                size="small"
                                sx={customSx}
                            />
                        );
                    },
                },
                {
                    field: "dataType",
                    headerName: "Type",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "acquisitionVector",
                    headerName: "Acquisition Vector",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "processingLeadUuid",
                    headerName: "Processing Lead",
                    minWidth: 160,
                    flex: 2,
                    editable: true,
                    type: "singleSelect",
                    sortComparator: (userUuidA, userUuidB) =>
                        userNamesComparator(
                            userUuidA,
                            userUuidB,
                            users,
                            invalidUuid,
                            "No Processing Lead"
                        ),
                    valueOptions: [
                        ...users.map((user) => ({
                            value: user.uuid,
                            label: `${user.firstName} ${user.lastName}`,
                        })),
                        { value: invalidUuid, label: "No processing lead" },
                    ],
                    sortable: false,
                    filterable: false,
                },
                {
                    field: "status",
                    headerName: "Status",
                    minWidth: 160,
                    flex: 2,
                    sortable: false,
                    filterable: false,
                },
            ],
        [
            users,
            setSearchParams,
            isBasicRole,
            get,
            navigate,
            openEditMission,
            missions?.rows,
            theme.palette,
        ]
    );

    const selectedItems = useMemo(() => {
        return {
            company: companies
                ? companies.rows.find(
                      (company) => company.uuid === filterParams.companyUuid
                  )
                : null,
            contract: contracts
                ? contracts.rows.find(
                      (contract) => contract.uuid === filterParams.contractUuid
                  )
                : null,
            site: sites
                ? sites.rows.find((site) => site.uuid === filterParams.siteUuid)
                : null,
            processingLead: users
                ? users.find(
                      (user) => user.uuid === filterParams.processingLeadUuid
                  )
                : null,
        };
    }, [
        companies,
        contracts,
        sites,
        users,
        filterParams.companyUuid,
        filterParams.contractUuid,
        filterParams.siteUuid,
        filterParams.processingLeadUuid,
    ]);

    const mergedFetchError =
        usersFetchError ??
        missionsFetchError ??
        contractsFetchError ??
        sitesFetchError ??
        companiesFetchError;

    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;

    if (!missions || !users || !companies) {
        return <TrafficTableSkeleton />;
    }

    return (
        <>
            <Stack justifyContent="space-between" mb={2} spacing={1}>
                <Stack
                    justifyContent="space-between"
                    direction="row"
                    spacing={1}
                >
                    <Typography variant="h4" gutterBottom>
                        Inbound Traffic
                    </Typography>
                    <FormControlLabel
                        id="showAllTraffic"
                        name="showAllTraffic"
                        control={<Switch />}
                        label="Show Only Current User's Traffic"
                        labelPlacement="end"
                        checked={!filterParams.showAllTraffic}
                        onChange={(_) => {
                            setSearchParams((prevState) => {
                                const newSearchParams = new URLSearchParams(
                                    prevState
                                );
                                if (filterParams.showAllTraffic)
                                    newSearchParams.delete("showAllTraffic");
                                else
                                    newSearchParams.set(
                                        "showAllTraffic",
                                        !filterParams.showAllTraffic
                                    );
                                return newSearchParams;
                            });
                        }}
                    />
                </Stack>
                <Grid container spacing={1}>
                    <Grid size={3}>
                        <Autocomplete
                            id="company"
                            value={selectedItems.company ?? null}
                            options={companies.rows}
                            isOptionEqualToValue={(option, value) =>
                                option.uuid === value.uuid
                            }
                            getOptionLabel={(option) => option.name}
                            onChange={(_, value) => {
                                setSearchParams((prevState) => {
                                    const newSearchParams = new URLSearchParams(
                                        prevState
                                    );
                                    if (value) {
                                        if (
                                            value &&
                                            selectedItems.contract
                                                ?.companyUuid !== value.uuid
                                        ) {
                                            newSearchParams.delete(
                                                "missionUuid"
                                            );
                                            newSearchParams.delete("siteUuid");
                                            newSearchParams.delete(
                                                "contractUuid"
                                            );
                                        }
                                        newSearchParams.set(
                                            "companyUuid",
                                            value.uuid
                                        );
                                    } else {
                                        newSearchParams.delete("companyUuid");
                                    }
                                    return newSearchParams;
                                });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Filter on company"
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Autocomplete
                            id="contract"
                            value={selectedItems.contract ?? null}
                            options={contracts?.rows ?? []}
                            isOptionEqualToValue={(option, value) =>
                                option.uuid === value.uuid
                            }
                            getOptionLabel={(option) => option.name}
                            getOptionKey={(option) => option.uuid}
                            onChange={(_, value) => {
                                setSearchParams((prevState) => {
                                    const newSearchParams = new URLSearchParams(
                                        prevState
                                    );
                                    if (value) {
                                        newSearchParams.delete("siteUuid");
                                        newSearchParams.set(
                                            "contractUuid",
                                            value.uuid
                                        );
                                    } else {
                                        newSearchParams.delete("siteUuid");
                                        newSearchParams.delete("contractUuid");
                                    }
                                    return newSearchParams;
                                });
                            }}
                            disabled={!contracts}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Filter on campaign"
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Autocomplete
                            id="site"
                            options={sites?.rows ?? []}
                            value={selectedItems.site ?? null}
                            isOptionEqualToValue={(option, value) =>
                                option.uuid === value.uuid
                            }
                            getOptionLabel={(option) => option.displayName}
                            getOptionKey={(option) => option.uuid}
                            onChange={(_, value) => {
                                setSearchParams((prevState) => {
                                    const newSearchParams = new URLSearchParams(
                                        prevState
                                    );
                                    if (value) {
                                        newSearchParams.set(
                                            "siteUuid",
                                            value.uuid
                                        );
                                    } else {
                                        newSearchParams.delete("siteUuid");
                                    }
                                    return newSearchParams;
                                });
                            }}
                            disabled={!filterParams.contractUuid || !sites}
                            renderInput={(params) => (
                                <TextField {...params} label="Filter on site" />
                            )}
                        />
                    </Grid>
                    {!filterParams.showAllTraffic && (
                        <Grid size={3}>
                            <Autocomplete
                                id="processingLead"
                                options={users}
                                value={selectedItems.processingLead ?? null}
                                isOptionEqualToValue={(option, value) =>
                                    option.uuid === value.uuid
                                }
                                getOptionLabel={(option) =>
                                    `${option.firstName} ${option.lastName}`
                                }
                                onChange={(_, value) => {
                                    setSearchParams((prevState) => {
                                        const newSearchParams =
                                            new URLSearchParams(prevState);
                                        if (value) {
                                            newSearchParams.set(
                                                "processingLeadUuid",
                                                value.uuid
                                            );
                                        } else {
                                            newSearchParams.delete(
                                                "processingLeadUuid"
                                            );
                                        }
                                        return newSearchParams;
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Filter on processing lead"
                                    />
                                )}
                            />
                        </Grid>
                    )}
                    <Grid size={3}>
                        <Autocomplete
                            id="pipelineInstanceStatus"
                            value={filterParams.pipelineInstanceStatus ?? null}
                            options={Object.values(PIPELINE_INSTANCE_STATUS)}
                            onChange={(_, value) => {
                                setSearchParams((prevState) => {
                                    const newSearchParams = new URLSearchParams(
                                        prevState
                                    );
                                    if (value) {
                                        newSearchParams.set(
                                            "pipelineInstanceStatus",
                                            value
                                        );
                                    } else {
                                        newSearchParams.delete(
                                            "pipelineInstanceStatus"
                                        );
                                    }
                                    return newSearchParams;
                                });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Filter on orthoimage status"
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </Stack>
            <Card>
                <Scrollbar>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <DataGrid
                            rowHeight={65}
                            rows={rows}
                            columns={columns}
                            pageSizeOptions={pageSizeOptions}
                            initialState={initialState}
                            paginationMode="server"
                            paginationModel={paginationModel}
                            onPaginationModelChange={updatePagination}
                            rowCount={missions.count}
                            sortingMode="server"
                            onSortModelChange={updateSort}
                            processRowUpdate={processRowUpdate}
                            onProcessRowUpdateError={
                                handleProcessRowUpdateError
                            }
                            loading={isLagging}
                            columnVisibilityModel={visibilityModel}
                            onColumnVisibilityModelChange={(newModel) =>
                                setVisibilityModel(newModel)
                            }
                        />
                    </div>
                </Scrollbar>
            </Card>
            <Dialog
                open={Boolean(alignmentReportMissionUuid)}
                fullWidth
                maxWidth="xl"
            >
                <AlignmentModal
                    missionUuid={alignmentReportMissionUuid}
                    onClose={alignmentModalOnClose}
                />
            </Dialog>
            <Dialog open={openEditMission} fullWidth maxWidth="md">
                <RoutingDialog
                    uuid={editMissionUuid}
                    closeModal={() => {
                        setOpenEditMission(!openEditMission);
                    }}
                    mutateTable={mutate}
                />
            </Dialog>
        </>
    );
}

const pageItemCount = 50;
const pageSizeOptions = [pageItemCount];

const invalidUuid = "invalid uuid";

const initialState = {
    sorting: { sortModel: [{ field: "uploadDate", sort: "desc" }] },
    columns: { columnVisibilityModel: { status: false } },
    pagination: { paginationModel: { page: 0, pageSize: pageSizeOptions[0] } },
};

const sortMap = {
    acquisitionDate: "date",
    uploadDate: "createdAt",
    receptionTime: "createdAt",
};
